import { SearchShortcut } from "./components/utils/SearchShortcut";
import { PrivacyPage } from "./pages/privacy/PrivacyPage";
import { getWorkspaceTheme } from "./workspaceTheme";
import { RoundAddressRouter } from "./RoundAddressRouter";
import { TeamsLoading } from "./pages/teams/TeamsLoading";
import { SavedApplicationLoading } from "@client/components/applications/ApplicationLoading";
import { useSearchParamsWithoutEmptyQuery } from "@client/hooks/use-search-params-without-empty-query";
import { ApplicationPreviewLoading } from "@client/components/applications/ApplicationPreviewLoading";
import { Capacitor } from "@capacitor/core";
import { AuthWrapper } from "@lib/auth/client";
import { isDeveloperEmail } from "@lib/developers";
import { TooltipProvider, Skeleton, ThemeProvider, Toaster } from "@sfc/ui";
import "microtip/microtip.css";
import {
  type ComponentType,
  lazy,
  type ReactNode,
  Suspense,
  useEffect,
} from "react";
import { Route, Switch, useLocation } from "wouter";
import {
  lazyWithBackgroundWarm,
  registerWarmChunk,
  warmChunkNowForInitialPath,
} from "./utils/backgroundChunkWarming";
import { APP_SHELL_CLASSNAME } from "./appShellClassName";

// The note editor's markdown<->HTML converter loads on demand from inside a
// lazy chunk — registering its warm there can land after the idle pass has
// already drained the registry. Register from this eager module instead:
// offline note editing needs the chunk cached even if no editor was opened
// while online. (Vite dedupes this specifier with the editor's own import().)
registerWarmChunk(() => import("@lib/noteMarkdown"));
import { DevDebugPanel } from "./components/utils/DevDebugPanel";
import { ErrorBoundary } from "./components/utils/ErrorBoundary";
import { IPhoneCrashScreen } from "./iphone/components/IPhoneCrashScreen";
import { IPhonePresenceRecorder } from "./iphone/IPhonePresenceRecorder";
import { useActionViewerScope } from "@client/actions/useAction";
import { useEffectiveUser } from "@client/hooks/use-effective-user";
import LoadingSpinner from "./components/utils/LoadingSpinner";
import { NotFoundCard } from "./components/utils/NotFoundCard";
import { OutOfDateVersionWarning } from "./components/utils/OutOfDateVersionWarning";
import { PresenceRecorder } from "./presence/PresenceRecorder";
import { Page } from "./components/utils/Page";
import { SiteHeader } from "./components/utils/SiteHeader";
import { HeaderConnectionProvider } from "./HeaderConnectionContext";
import { ImpersonateProvider } from "./ImpersonateContext";
import { useImpersonationRefValue } from "./ImperativeState";
import { useMobileAuthDeepLink } from "./iphone/lib/useMobileAuthDeepLink";
import { useUniversalLinkOpen } from "./iphone/lib/useUniversalLinkOpen";
import { isIPad } from "./lib/isIPad";
import {
  applyLastVisitedRoundBootRedirect,
  TrackLastVisitedRound,
} from "./lib/lastVisitedRound";
import { readCachedCurrentUser } from "./offline/offlineCache";
import { Home } from "./pages/home/Home";
import { Round } from "./pages/round/Round";
import { QueueProvider } from "./pages/round/funder-workspace/queue/QueueProvider";
import { useOptionalRoundRoute } from "./RoundRouteContext";

// The iPhone app's UI tree rides its own chunk, keeping it out of the
// desktop/iPad startup parse. The native entry (main-mobile.tsx) resolves it
// BEFORE the first render and passes it as the `iphoneApp` prop — React holds
// a committed Suspense fallback for its ~300ms throttle even when the chunk
// resolves instantly. The lazy path serves the dev rig, which reaches this
// branch through the desktop entry (CAP_SERVER_URL serving); plain lazy()
// rather than the warm-registered helper, since no surface that runs the
// background warm can ever render the iPhone app.
const LazyIPhoneApp = lazy(async () => ({
  default: (await import("./iphone/IPhoneApp")).IPhoneApp,
}));
import { SignOut } from "./pages/signout/SignOut";
import { getDocumentTitleForPath } from "./utils/pageTitle";

// Admin Tools and the dev page are role-gated, and the Admin Tools chunk is
// the largest route chunk in the app — background-warming them for a funder
// spends the first visit's bandwidth and main-thread evaluate time on pages
// they can never open. The warmIf gates read the cached identity (written by
// useCurrentUser on every authenticated load, so it's populated well before
// the post-startup warm reaches these chunks); an absent cache reads as
// "don't warm", and the page still loads on demand when actually visited.
const TeamInvitationPage = lazyWithBackgroundWarm(async () => {
  const { TeamInvitationPage } = await import("./pages/teams/TeamInvitations");
  return { default: TeamInvitationPage };
});

const TeamsPage = lazyWithBackgroundWarm(async () => {
  const { TeamsPage } = await import("./pages/teams/TeamsPage");
  return { default: TeamsPage };
});

const AdminToolsPage = lazyWithBackgroundWarm(
  async () => {
    const { AdminToolsPage } =
      await import("./pages/admin-tools/AdminToolsPage");
    return { default: AdminToolsPage };
  },
  { warmIf: () => readCachedCurrentUser()?.isSfcAdmin === true },
);

// The Application Builder is SFC-admin only too, and carries the Yjs
// collaboration stack, so it warms on the same identity check as Admin Tools.
const ApplicationBuilderPage = lazyWithBackgroundWarm(
  async () => {
    const { ApplicationBuilderPage } =
      await import("./pages/application-builder/ApplicationBuilderPage");
    return { default: ApplicationBuilderPage };
  },
  { warmIf: () => readCachedCurrentUser()?.isSfcAdmin === true },
);

// Filling out a published form. SFC-admin gated for now (same as the
// builder), but its own chunk: it renders a published version and never
// loads the Yjs draft stack.
const SavedApplicationPage = lazyWithBackgroundWarm(async () => {
  const { SavedApplicationPage } =
    await import("./pages/application-builder/SavedApplicationPage");
  return { default: SavedApplicationPage };
});
const TeamApplicationPage = lazyWithBackgroundWarm(async () => {
  const { TeamApplicationPage } =
    await import("./pages/application-builder/TeamApplicationPage");
  return { default: TeamApplicationPage };
});

const FormFillPage = lazyWithBackgroundWarm(
  async () => {
    const { FormFillPage } =
      await import("./pages/application-builder/FormFillPage");
    return { default: FormFillPage };
  },
  { warmIf: () => readCachedCurrentUser()?.isSfcAdmin === true },
);

const DevPage = lazyWithBackgroundWarm(
  async () => {
    const { DevPage } = await import("./pages/dev/DevPage");
    return { default: DevPage };
  },
  { warmIf: () => isDeveloperEmail(readCachedCurrentUser()?.email) },
);

// Applicant-facing stencil (will list the signed-in user's applications
// across rounds). Open to any authenticated user, so no warmIf gate.
const ApplicantPage = lazyWithBackgroundWarm(async () => ({
  default: (await import("./pages/applicant/ApplicantPage")).ApplicantPage,
}));

// Open to any authenticated user (no warmIf gate), and its own chunk: it
// carries the whole apply/preview flow (form guides, deadline banners, the
// yjs-backed form-guide search), which otherwise keeps growing the bundle
// every route has to parse before becoming usable.
const SignedInFundingPage = lazyWithBackgroundWarm(async () => ({
  default: (await import("@client/components/applications/OpenApplications"))
    .SignedInFundingPage,
}));

const loadProfilePage = async () => ({
  default: (await import("./pages/profile/Profile")).ProfilePage,
});
const ProfilePage = lazyWithBackgroundWarm(loadProfilePage);

warmChunkNowForInitialPath(ProfilePage.preload, (path) => path === "/settings");

// Wraps the header + routes in QueueProvider so the navbar QueueDrawer and the
// round pages share one store instance. Keyed on (roundId, viewAsUserId) so an
// impersonation or round switch remounts a clean in-memory store.
//
// roundId comes from the resolved route — NOT ImpersonateContext's `value.round`
// — because `Round` (a descendant) sets that context value on mount and clears
// it on unmount. Keying this ancestor on it would loop infinitely: remount ->
// Round unmounts -> value cleared -> key changes -> remount -> ... The resolved
// identity is stable across Round's mount/unmount. viewAsUserId comes from the impersonation
// ref, which (unlike the context value) is not cleared on RoundWrapper unmount.
// Some iPads report env(safe-area-inset-top) as 0 in landscape even though
// the status bar still overlays the WebView, so floor the shell's top inset
// (--safe-area-top in @sfc/ui styles.css) at the status-bar height. iPhone
// is excluded: iOS hides its status bar in landscape, where a floor would
// wrongly pad. Module scope so it applies before first paint.
if (Capacitor.isNativePlatform() && isIPad()) {
  document.documentElement.style.setProperty("--min-safe-area-top", "24px");
}

// A cold start of the iPad shell always loads at "/" — rewrite it to the
// last-visited round before the router mounts (module scope = pre-render), so
// reopening the app lands back in the round with no Home flash. No-op outside
// the native iPad shell.
applyLastVisitedRoundBootRedirect();

function QueueBoundary({ children }: { children: ReactNode }) {
  const viewAsUserId = useImpersonationRefValue();
  const roundId = useOptionalRoundRoute()?.roundId ?? null;

  return (
    <QueueProvider
      key={`${roundId ?? "none"}-${viewAsUserId ?? "self"}`}
      roundId={roundId}
    >
      {children}
    </QueueProvider>
  );
}

type AppProps = {
  // The native iPhone entry resolves the iPhone UI chunk before rendering and
  // passes it here, so it paints synchronously (see LazyIPhoneApp above).
  iphoneApp?: ComponentType;
};

export function App(props: AppProps = {}) {
  useMobileAuthDeepLink();
  useUniversalLinkOpen();

  return (
    <Switch>
      <Route path="/privacy">
        <ThemeProvider>
          <PrivacyPage />
          <Toaster />
        </ThemeProvider>
      </Route>
      <Route>
        <AccountApp {...props} />
      </Route>
    </Switch>
  );
}

function AccountApp(props: AppProps) {
  // Scope in-flight action state to whoever is being viewed AS. Impersonation
  // is app-scoped and changes request identity with no reload, so without this
  // one viewer's pending work — and its owned poll, now authenticated as
  // somebody else — would survive into the next viewer's session.
  useActionViewerScope(useEffectiveUser().data?.userId ?? null);

  return (
    <>
      <span hidden data-workspace-theme="green" />
      <SearchShortcut />
      <AppContent {...props} />
    </>
  );
}

function AppContent({ iphoneApp: IPhoneAppComponent }: AppProps) {
  if (Capacitor.isNativePlatform() && !isIPad()) {
    return (
      <ThemeProvider>
        {/* Outside the Suspense so a failed lazy-chunk load is caught too;
            inside the ThemeProvider so the crash screen keeps app styling.
            The boundary reports to Sentry (SP-2628); the crash screen's
            reload button is the recovery path a chromeless shell lacks. */}
        <ErrorBoundary fallback={<IPhoneCrashScreen />}>
          <Suspense fallback={null}>
            {IPhoneAppComponent != null ? (
              <IPhoneAppComponent />
            ) : (
              <LazyIPhoneApp />
            )}
          </Suspense>
        </ErrorBoundary>
        <IPhonePresenceRecorder />
        <DevDebugPanel />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <TooltipProvider delay={0}>
        <div id="app" className={APP_SHELL_CLASSNAME}>
          {/* Remembers the round the user is in so a cold start of the iPad
            shell can reopen it (see main-mobile.tsx). No-op outside the
            native iPad shell. A null leaf rather than a hook in App: its
            router subscription must not re-render the whole tree on every
            navigation. */}
          <OutOfDateVersionWarning />
          <HeaderConnectionProvider>
            <ImpersonateProvider>
              <AuthWrapper
                renderFallback={renderAuthShell}
                loading={<ApplicationRouteFallback />}
              >
                <RoundAddressRouter renderFallback={renderRouteShell}>
                  <AppRouteShell>
                    <Root />
                  </AppRouteShell>
                </RoundAddressRouter>
              </AuthWrapper>
              <Toaster
                style={{
                  zIndex: 10_001 /* modal uses 10_000 */,
                  pointerEvents: "auto",
                }}
              />
              <PresenceRecorder />
            </ImpersonateProvider>
          </HeaderConnectionProvider>
          <DevDebugPanel />
        </div>
      </TooltipProvider>
    </ThemeProvider>
  );
}

function AppRouteShell({
  children,
  isAuth = false,
}: {
  children: ReactNode;
  isAuth?: boolean;
}) {
  const [location] = useLocation();
  const isApplicationPage =
    /^\/(application-builder|applications|forms|applicant|apply)(?:\/|$)/.test(
      location,
    ) || /^\/round\/[^/]+\/applications(?:\/|$)/.test(location);
  return (
    <QueueBoundary>
      <span
        hidden
        data-workspace-theme={isAuth ? "green" : getWorkspaceTheme(location)}
      />
      {isApplicationPage && <span hidden data-application-theme="green" />}
      <TrackLastVisitedRound />
      <AppHeader />
      {children}
    </QueueBoundary>
  );
}
function renderAuthShell(content: ReactNode) {
  return <AppRouteShell isAuth>{content}</AppRouteShell>;
}
function renderRouteShell(content: ReactNode) {
  return <AppRouteShell>{content}</AppRouteShell>;
}

function AppHeader() {
  return (
    <div className="shrink-0">
      <SiteHeader />
    </div>
  );
}

function AppNotFoundPage() {
  const [location] = useLocation();

  return (
    <Page
      title="Page not found"
      description="The link you entered doesn't match an S-Process page."
    >
      <NotFoundCard
        currentPath={location}
        primaryHref="/"
        primaryLabel="Go to homepage"
      />
    </Page>
  );
}

export function Root() {
  const [location] = useLocation();

  useEffect(() => {
    if (location.startsWith("/round/")) {
      return;
    }
    document.title = getDocumentTitleForPath(location);
  }, [location]);

  return (
    <>
      <Suspense fallback={<ApplicationRouteFallback path={location} />}>
        <Switch>
          <Route path="/settings">
            {() => (
              <Suspense fallback={<ProfileLoadingSkeleton />}>
                <ProfilePage />
              </Suspense>
            )}
          </Route>
          <Route path="/round/:roundReference" component={Round} nest />
          <Route path="/team-invitations/:teamId/:invitationId">
            {(params) => <TeamInvitationPage {...params} />}
          </Route>
          <Route path="/teams" nest>
            <TeamsPage />
          </Route>
          <Route path="/admin" component={AdminToolsPage} nest />
          <Route path="/application-builder" nest>
            <ApplicationBuilderPage />
          </Route>
          <Route
            path="/applications/:responseId"
            component={SavedApplicationPage}
          />
          <Route path="/apply" component={SignedInFundingPage} />
          <Route path="/apply/:roundId" component={TeamApplicationPage} />
          <Route
            path="/apply/:roundId/:slug/team"
            component={TeamApplicationPage}
          />
          <Route path="/apply/:roundId/:slug" component={FormFillPage} />
          <Route path="/applicant" component={ApplicantPage} />
          <Route path="/dev" component={DevPage} />
          <Route path="/signout" component={SignOut} />
          <Route path="/" component={Home} />
          <Route path="*" component={AppNotFoundPage} />
        </Switch>
      </Suspense>
    </>
  );
}

function ProfileLoadingSkeleton() {
  return (
    <Page
      title="Settings"
      description="Your profile, preferences and agent access."
    >
      <div
        className="space-y-6"
        role="status"
        aria-label="Loading settings"
        aria-busy="true"
      >
        <div className="rounded-lg border border-border-subtle p-4 md:p-6">
          <Skeleton className="mb-4 h-4 w-56" />
          <div className="flex flex-col gap-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-9 w-full" />
            </div>
            <Skeleton className="h-9 w-20 self-end" />
          </div>
        </div>
        <div className="rounded-lg border border-border-subtle p-4 md:p-6">
          <Skeleton className="h-5 w-28" />
          <div className="mt-3 flex items-center justify-between gap-4">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-full max-w-md" />
            </div>
            <Skeleton className="h-6 w-11 rounded-full" />
          </div>
        </div>
        <div className="rounded-lg border border-border-subtle p-4 md:p-6">
          <Skeleton className="h-5 w-28" />
          <Skeleton className="mt-3 h-4 w-full max-w-2xl" />
          <Skeleton className="mt-2 h-4 w-3/4 max-w-xl" />
        </div>
      </div>
    </Page>
  );
}

function ApplicationRouteFallback({ path: suppliedPath }: { path?: string }) {
  const [location] = useLocation();
  const path = suppliedPath ?? location;
  const [params] = useSearchParamsWithoutEmptyQuery();
  if (path === "/teams" || path.startsWith("/teams/"))
    return <TeamsLoading admin={false} detail={!/^\/teams\/?$/.test(path)} />;
  if (path.startsWith("/applications/"))
    return <SavedApplicationLoading editing={params.get("edit") === "1"} />;
  if (path.startsWith("/apply/")) return <ApplicationPreviewLoading />;
  return <LoadingSpinner />;
}
